import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Row, Typography, Card, Descriptions, Tag, Spin, Button, Modal, Space } from "antd";
import { ReloadOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import moment from "moment";

import { retrySystemNotificationRequest } from "providers/SystemNotificationProvider/slice";
import BackButton from "components/BackButton";
import Helper from "utils/helpers";
import "./styles.less";

const getProcessingStatusTag = (status) => {
  switch (status) {
    case "completed":
      return <Tag color="green">Hoàn thành</Tag>;
    case "processing":
      return <Tag color="blue">Đang gửi</Tag>;
    case "pending":
      return <Tag color="orange">Chờ gửi</Tag>;
    default:
      return <Tag>{status || "N/A"}</Tag>;
  }
};

const getRetryStatusTag = (retryStatus, retryCount) => {
  if (!retryStatus || retryStatus === "none") {
    return <Typography.Text type="secondary">Chưa retry</Typography.Text>;
  }
  const countText = retryCount > 0 ? ` (${retryCount} lần)` : "";
  switch (retryStatus) {
    case "completed":
      return <Tag color="green">Hoàn thành{countText}</Tag>;
    case "processing":
      return <Tag color="blue">Đang retry{countText}</Tag>;
    case "pending":
      return <Tag color="orange">Chờ retry{countText}</Tag>;
    default:
      return <Tag>{retryStatus}{countText}</Tag>;
  }
};

const getSuccessRateColor = (rate) => {
  if (rate >= 90) return "green";
  if (rate >= 70) return "orange";
  return "red";
};

const SystemNotificationDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [notification, setNotification] = useState(null);

  const notificationList = useSelector((state) =>
    state.systemNotification.notificationList
  );
  const isLoading = useSelector((state) => state.systemNotification.isLoading);

  useEffect(() => {
    const foundNotification = notificationList.find(item => item._id === id);

    if (foundNotification) {
      setNotification(foundNotification);
    } else {
      navigate("/system_notifications");
    }
  }, [id, notificationList, navigate]);

  const canRetry = notification &&
    notification.failedDevices > 0 &&
    notification.retryStatus !== "processing" &&
    notification.processingStatus === "completed";

  const handleRetry = () => {
    Modal.confirm({
      title: "Xác nhận gửi lại",
      icon: <ExclamationCircleOutlined />,
      content: "Bạn có chắc chắn muốn gửi lại thông báo cho các thiết bị thất bại?",
      okText: "Gửi lại",
      cancelText: "Hủy",
      onOk: () => {
        dispatch(retrySystemNotificationRequest(id))
          .then(() => {
            Helper.toastr("Thành công", "Đã bắt đầu gửi lại thông báo", "success");
          })
          .catch(() => {
            Helper.toastr("Thất bại", "Có lỗi xảy ra khi gửi lại. Vui lòng thử lại.", "error");
          });
      },
    });
  };

  if (!notification) {
    return (
      <div className="system-notification-detail-page">
        <Row className="system-notification-detail-header">
          <BackButton />
          <Typography.Title level={4}>Chi tiết thông báo hệ thống</Typography.Title>
        </Row>
        <div className="detail-content">
          <Card>
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>Đang tải dữ liệu...</div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="system-notification-detail-page">
      <Row className="system-notification-detail-header" justify="space-between" align="middle">
        <Space>
          <BackButton backUrl="/system_notifications" />
          <Typography.Title level={4} style={{ marginBottom: 0 }}>Chi tiết thông báo hệ thống</Typography.Title>
        </Space>
        {canRetry && (
          <Button
            type="primary"
            danger
            icon={<ReloadOutlined />}
            onClick={handleRetry}
            loading={isLoading}
          >
            Gửi lại ({notification.failedDevices} thiết bị thất bại)
          </Button>
        )}
      </Row>

      <div className="detail-content">
        <Card>
          <Descriptions
            title="Thông tin thông báo"
            bordered
            column={1}
            labelStyle={{ fontWeight: 500, width: "200px" }}
          >
            <Descriptions.Item label="Tiêu đề">
              <Typography.Text strong>
                {notification.title || notification.metaData?.title || "N/A"}
              </Typography.Text>
            </Descriptions.Item>

            <Descriptions.Item label="Nội dung">
              <div className="notification-message">
                {notification.message ? (
                  <div dangerouslySetInnerHTML={{ __html: notification.message }} />
                ) : (
                  <Typography.Text type="secondary">N/A</Typography.Text>
                )}
              </div>
            </Descriptions.Item>

            <Descriptions.Item label="Số người nhận được">
              <Tag color="orange" style={{ fontWeight: 500 }}>
                {notification.affectedUsers || 0} người
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item label="SL thiết bị nhận thông báo">
              <Tag color="blue" style={{ fontWeight: 500 }}>
                {notification.numberOfDevices || 0} thiết bị
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item label="Thiết bị thành công">
              <Tag color="green" style={{ fontWeight: 500 }}>
                {notification.successDevices || 0} thiết bị
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item label="Thiết bị thất bại">
              {notification.failedDevices > 0 ? (
                <Tag color="red" style={{ fontWeight: 500 }}>
                  {notification.failedDevices} thiết bị
                </Tag>
              ) : (
                <Tag style={{ fontWeight: 500 }}>0 thiết bị</Tag>
              )}
            </Descriptions.Item>

            {notification.successRate != null && (
              <Descriptions.Item label="Tỷ lệ thành công">
                <Tag color={getSuccessRateColor(notification.successRate)} style={{ fontWeight: 500 }}>
                  {notification.successRate}%
                </Tag>
              </Descriptions.Item>
            )}

            <Descriptions.Item label="Trạng thái gửi">
              {getProcessingStatusTag(notification.processingStatus)}
            </Descriptions.Item>

            <Descriptions.Item label="Trạng thái retry">
              {getRetryStatusTag(notification.retryStatus, notification.retryCount)}
            </Descriptions.Item>

            <Descriptions.Item label="Ngày tạo">
              <Typography.Text>
                {notification.createdAt
                  ? moment(notification.createdAt).format("DD/MM/YYYY HH:mm:ss")
                  : "N/A"}
              </Typography.Text>
            </Descriptions.Item>

            {notification.updatedAt && (
              <Descriptions.Item label="Ngày cập nhật">
                <Typography.Text>
                  {moment(notification.updatedAt).format("DD/MM/YYYY HH:mm:ss")}
                </Typography.Text>
              </Descriptions.Item>
            )}

            {notification.type && (
              <Descriptions.Item label="Loại thông báo">
                <Tag color="blue">{notification.type}</Tag>
              </Descriptions.Item>
            )}

            {notification.priority && (
              <Descriptions.Item label="Độ ưu tiên">
                <Tag color={
                  notification.priority === "high" ? "red" :
                  notification.priority === "normal" ? "blue" :
                  "green"
                }>
                  {notification.priority === "high" ? "Cao" :
                   notification.priority === "normal" ? "Bình thường" :
                   "Thấp"}
                </Tag>
              </Descriptions.Item>
            )}

            {notification.status && (
              <Descriptions.Item label="Trạng thái">
                <Tag color={notification.status === "active" ? "green" : "red"}>
                  {notification.status === "active" ? "Đang hoạt động" : "Không hoạt động"}
                </Tag>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      </div>
    </div>
  );
};

export default SystemNotificationDetail;
